import "../party/partyShell.css";

import EmailIcon from "@mui/icons-material/Email";
import InstagramIcon from "@mui/icons-material/Instagram";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import PageSeo from "../components/PageSeo";
import { useAppSubmitting } from "../hooks/useAppSubmitting";
import { useUser, useUserCredits } from "../hooks/useAuth";
import { useIsMobileApp, useMobileAppPlatform } from "../hooks/useIsMobileApp";
import {
  PartyAccessBadge,
  PartyCoinBadge,
} from "../party/PartyChrome";
import { PartyConfettiCards, PartyMascot } from "../party/PartyMascot";
import { PartyTvCastGlyph } from "../party/PartyTvCastGlyph";
import { getFeaturedGame, usePartyShellGames } from "../party/usePartyShellGames";
import { MixpanelEvents, trackEvent } from "../utils/mixpanel";
import { absoluteUrl } from "../utils/siteOrigin";
import { trackTiktokCompletePayment } from "../utils/tiktokPixel";

const APP_STORE_URL =
  "https://apps.apple.com/us/app/%D9%83%D8%A7%D9%86-kan/id6759281751";

/** Session tab: show download-app modal at most once (same audience as footer App Store badge). */
const DOWNLOAD_APP_PROMPT_SESSION_KEY = "kan_landing_download_app_prompt_v1";

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const isMobileApp = useIsMobileApp();
  const { isNotMobileAppAndroid } = useMobileAppPlatform();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refetch: refetchCredits } = useUserCredits();
  const { data: currentUser } = useUser();
  const { isAppSubmitting } = useAppSubmitting();
  const isLoggedIn = Boolean(currentUser);
  const { games, credits, isLoading, isError } = usePartyShellGames();
  const featured = getFeaturedGame(games);

  const [paymentNotification, setPaymentNotification] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  const [downloadAppPromptOpen, setDownloadAppPromptOpen] = useState(false);
  /** Avoid duplicate payment handling (e.g. React Strict Mode double effect). */
  const paymentReturnHandledRef = useRef(false);

  const landingCta = useCallback((ctaId: string, props?: Record<string, unknown>) => {
    trackEvent(MixpanelEvents.LANDING_CTA_CLICKED, { cta_id: ctaId, ...props });
  }, []);

  const goPurchase = useCallback(() => {
    if (!isLoggedIn) {
      navigate(`/login?next=${encodeURIComponent("/purchase")}`);
      return;
    }
    navigate("/purchase");
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    trackEvent(MixpanelEvents.PAGE_VIEWED, {
      page_name: "Landing Page (party)",
      path: "/",
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isMobileApp || !isNotMobileAppAndroid) return;
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      try {
        if (sessionStorage.getItem(DOWNLOAD_APP_PROMPT_SESSION_KEY) === "1") return;
        sessionStorage.setItem(DOWNLOAD_APP_PROMPT_SESSION_KEY, "1");
      } catch {
        /* private mode / blocked storage — still show once this load */
      }
      trackEvent(MixpanelEvents.LANDING_DOWNLOAD_APP_PROMPT_SHOWN, {
        page_name: "Landing Page (party)",
        path: "/",
      });
      setDownloadAppPromptOpen(true);
    }, 900);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [isMobileApp, isNotMobileAppAndroid]);

  const dismissDownloadAppPrompt = useCallback(() => {
    trackEvent(MixpanelEvents.LANDING_DOWNLOAD_APP_PROMPT_DISMISSED, {
      page_name: "Landing Page (party)",
      path: "/",
    });
    setDownloadAppPromptOpen(false);
  }, []);

  const onDownloadAppPromptAppStore = useCallback(() => {
    landingCta("download_app_prompt_app_store");
    setDownloadAppPromptOpen(false);
  }, [landingCta]);

  useEffect(() => {
    const paymentStatus = searchParams.get("paymentStatus");
    const message = searchParams.get("message");
    const total = searchParams.get("total");

    if (!paymentStatus) {
      paymentReturnHandledRef.current = false;
      return;
    }

    if (paymentStatus === "success") {
      if (paymentReturnHandledRef.current) {
        return;
      }
      paymentReturnHandledRef.current = true;

      void refetchCredits();
      trackEvent(MixpanelEvents.PAYMENT_RESULT_SUCCESS, {
        payment_status: paymentStatus,
        total_amount: total,
        order_number: searchParams.get("number"),
      });

      const rawTotal = total != null && total !== "" ? Number(total) : NaN;
      const value = Number.isFinite(rawTotal) ? rawTotal : 0;
      const orderNumber = searchParams.get("number");
      trackTiktokCompletePayment({
        value: Number(value) * 3,
        currency: "USD",
        ...(orderNumber ? { content_id: orderNumber } : {}),
      });

      const successMessage = total
        ? t("purchase.paymentSuccessWithAmount", {
          amount: total,
          defaultValue: `Payment successful! Amount: ${total} KWD`,
        })
        : t("purchase.paymentSuccess", "Payment successful!");
      setPaymentNotification({ type: "success", message: successMessage });
      const next = new URLSearchParams(searchParams);
      next.delete("paymentStatus");
      next.delete("message");
      next.delete("total");
      next.delete("number");
      setSearchParams(next, { replace: true });
    } else if (paymentStatus === "error" || paymentStatus === "failed") {
      const errorMessage =
        message || t("purchase.paymentFailed", "Payment failed. Please try again.");
      trackEvent(MixpanelEvents.PAYMENT_RESULT_FAILED, {
        payment_status: paymentStatus,
        error_message: message,
      });
      setPaymentNotification({ type: "error", message: errorMessage });
      const next = new URLSearchParams(searchParams);
      next.delete("paymentStatus");
      next.delete("message");
      next.delete("total");
      next.delete("number");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, refetchCredits, t]);

  useEffect(() => {
    if (!paymentNotification.type) return;
    const timer = window.setTimeout(() => {
      setPaymentNotification({ type: null, message: "" });
    }, 6000);
    return () => window.clearTimeout(timer);
  }, [paymentNotification.type]);

  const toneChip =
    featured?.tone === "mystery"
      ? t("landing.party.toneMystery")
      : t("landing.party.toneTeam");

  const landingJsonLd = useMemo(() => {
    const canonical = absoluteUrl("/");
    if (!canonical.startsWith("http")) return undefined;
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Kan",
      alternateName: ["كان", "KAN Party", "Kan party games"],
      url: canonical,
      inLanguage: i18n.language === "ar" ? ["ar", "en"] : ["en", "ar"],
      description: t("seo.landing.description"),
    };
  }, [t, i18n.language]);

  return (
    <div
      className="party-shell-root"
      dir={i18n.language === "ar" ? "rtl" : "ltr"}
      lang={i18n.language || "ar"}
      data-party-theme="midnight"
      data-party-type="rubik"
      style={{ minHeight: "100vh", width: "100%" }}
    >
      <PageSeo
        titleKey="seo.landing.title"
        descriptionKey="seo.landing.description"
        path="/"
        jsonLd={landingJsonLd}
      />
      {paymentNotification.type && (
        <Box
          sx={{
            position: "fixed",
            top: { xs: 12, sm: 16 },
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10000,
            minWidth: { xs: "90%", sm: 360 },
            maxWidth: 560,
          }}
        >
          <Alert
            severity={paymentNotification.type}
            onClose={() => setPaymentNotification({ type: null, message: "" })}
            sx={{
              boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
              borderRadius: 3,
            }}
          >
            {paymentNotification.message}
          </Alert>
        </Box>
      )}

      <Dialog
        disablePortal
        open={downloadAppPromptOpen}
        onClose={dismissDownloadAppPrompt}
        aria-labelledby="download-app-prompt-title"
        slotProps={{
          backdrop: { sx: { backdropFilter: "blur(6px)" } },
        }}
        PaperProps={{
          sx: {
            borderRadius: "var(--r-card, 28px)",
            maxWidth: 420,
            width: "100%",
            mx: 2,
            bgcolor: "var(--bg-2)",
            color: "var(--ink)",
            border: "1px solid var(--ink-faint)",
            boxShadow: "var(--shadow-card)",
          },
        }}
      >
        <DialogTitle id="download-app-prompt-title" className="party-display" sx={{ fontSize: "1.4rem", pr: 3 }}>
          {t("landing.party.downloadAppPromptTitle")}
        </DialogTitle>
        <DialogContent >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>

            <Typography sx={{ color: "var(--ink-dim)", fontSize: 15, lineHeight: 1.55, mb: 2 }}>
              {t("landing.party.downloadAppPromptBody")}
            </Typography>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onDownloadAppPromptAppStore}
              style={{ display: "inline-block", lineHeight: 0 }}
            >
              <img
                src={`https://tools.applemediaservices.com/api/badges/download-on-the-app-store/white/${i18n.language?.startsWith("ar") ? "ar-sa" : "en-us"}?size=250x83`}
                alt={t("landing.party.downloadAppAlt")}
                width={150}
                height={50}
                decoding="async"
                style={{ height: 50, width: "auto", maxWidth: "min(220px, 85vw)" }}
              />
            </a>
          </Box>
        </DialogContent >
        <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 3, pb: 2, justifyContent: "flex-end" }}>
          <Button variant="text" onClick={dismissDownloadAppPrompt} sx={{ color: "var(--ink-dim)", textTransform: "none" }}>
            {t("landing.party.downloadAppPromptLater")}
          </Button>
        </DialogActions>
      </Dialog>

      <div className="party-app-frame" style={{ margin: "0 auto" }}>
        <div style={{ position: "relative", paddingBottom: 40 }}>
          <PartyConfettiCards count={5} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 18px 4px",
              position: "relative",
              zIndex: 2,
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <PartyCoinBadge
                amount={credits}
                {...(!isAppSubmitting
                  ? {
                    onClick: () => {
                      landingCta("credits_badge", { logged_in: isLoggedIn });
                      isLoggedIn
                        ? navigate("/purchase")
                        : navigate(`/login?next=${encodeURIComponent("/purchase")}`);
                    },
                    ariaLabel: t("landing.party.purchaseCreditsAria"),
                  }
                  : {})}
              />
              {!isAppSubmitting && !isLoggedIn && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      landingCta("sign_in");
                      navigate(`/login?next=${encodeURIComponent("/")}`);
                    }}
                    className="party-chip"
                    style={{ cursor: "pointer", border: "1px solid var(--ink-faint)" }}
                  >
                    {t("landing.signInToPlay")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      landingCta("sign_up");
                      navigate(`/signup?next=${encodeURIComponent("/")}`);
                    }}
                    className="party-chip party-chip-free"
                    style={{ cursor: "pointer", border: "1px solid transparent" }}
                  >
                    {t("landing.party.signUp")}
                  </button>
                </>
              )}
              {isLoggedIn && !isAppSubmitting && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    landingCta("profile");
                    navigate("/update-profile");
                  }}
                  className="party-chip"
                  sx={{
                    cursor: "pointer",
                    borderColor: "var(--ink-faint)",
                    color: "var(--ink)",
                    textTransform: "none",
                    fontFamily: "inherit",
                    fontWeight: 700,
                    borderRadius: "var(--r-chip, 14px)",
                    px: 1.5,
                    py: 0.75,
                    minHeight: 36,
                  }}
                >
                  {t("landing.party.profileAccess")}
                </Button>
              )}
            </div>
            {!isAppSubmitting && (
              <button
                type="button"
                onClick={() => {
                  landingCta("header_shop");
                  goPurchase();
                }}
                style={{
                  appearance: "none",
                  border: "1.5px solid var(--ink-faint)",
                  background: "transparent",
                  color: "var(--ink)",
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label={t("landing.party.store")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M4 7h16l-1.5 11a2 2 0 01-2 1.8H7.5a2 2 0 01-2-1.8L4 7z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path d="M9 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>
            )}
          </div>

          <section style={{ padding: "18px 24px 8px", position: "relative", zIndex: 2 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
              <div className="party-mono" style={{ color: "var(--ink-dim)", fontSize: 12, letterSpacing: "0.08em" }}>
                {t("landing.party.wordmark")}
              </div>
              <div
                style={{
                  flex: 1,
                  borderTop: "1.5px dashed var(--ink-faint)",
                  marginBottom: 4,
                }}
              />
            </div>
            <h1
              className="party-display"
              style={{
                fontSize: "clamp(64px, 16vw, 112px)",
                margin: "6px 0 0",
                color: "var(--ink)",
              }}
            >
              {t("landing.party.headline")}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 700,
                  fontSize: 20,
                  color: "var(--ink-dim)",
                }}
              >
                {t("landing.party.tagline", { count: games.length || 0 })}
              </span>
              <span
                style={{
                  background: "var(--hot)",
                  color: "#fff",
                  padding: "4px 10px",
                  borderRadius: 999,
                  fontFamily: "var(--font-mono)",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.08em",
                }}
              >
                {t("landing.party.badge")}
              </span>
            </div>
            <p style={{ margin: "14px 0 0", color: "var(--ink-dim)", fontSize: 14, maxWidth: 420, lineHeight: 1.5 }}>
              {t("landing.subtitle")}
            </p>
          </section>

          {isError && (
            <div style={{ padding: "0 20px", color: "var(--ink-dim)", textAlign: "center" }}>
              {t("landing.party.loadError")}
            </div>
          )}

          {isLoading && games.length === 0 && !isError && (
            <div style={{ padding: 48, textAlign: "center", color: "var(--ink-dim)" }}>…</div>
          )}

          {featured && (
            <section style={{ padding: "24px 20px 8px", position: "relative", zIndex: 2 }}>
              <button
                type="button"
                onClick={() => {
                  landingCta("featured_game", { shell_id: featured.id, access: featured.access });
                  navigate(`/party/detail/${featured.id}`);
                }}
                className="party-kan-card party-anim-card-deal"
                style={
                  {
                    ["--card-bg" as string]: featured.bg,
                    width: "100%",
                    textAlign: "start",
                    padding: "22px 22px 20px",
                    cursor: "pointer",
                    appearance: "none",
                    border: 0,
                    color: "var(--ink)",
                    fontFamily: "inherit",
                    display: "flex",
                    alignItems: "stretch",
                    gap: 16,
                    position: "relative",
                  } as React.CSSProperties
                }
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    flex: 1,
                    minHeight: 180,
                  }}
                >
                  <div>
                    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                      <span
                        className="party-mono"
                        style={{
                          fontSize: 10,
                          letterSpacing: "0.1em",
                          color: "var(--ink-dim)",
                          padding: "4px 8px",
                          borderRadius: 6,
                          border: "1px solid var(--ink-faint)",
                        }}
                      >
                        {t("landing.party.featuredLabel")}
                      </span>
                      <PartyAccessBadge access={featured.access} price={featured.price} />
                    </div>
                    <div className="party-display" style={{ fontSize: 56, lineHeight: 0.9 }}>
                      {i18n.language === "ar" ? featured.nameAr : featured.nameEn}
                    </div>
                    <p
                      style={{
                        margin: "10px 0 0",
                        color: "var(--ink-dim)",
                        fontSize: 14,
                        maxWidth: "24ch",
                      }}
                    >
                      {featured.desc}
                    </p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                    <span className="party-chip">{featured.playersTag}</span>
                    <span className="party-chip">{toneChip}</span>
                    <span className="party-chip" style={{ display: "inline-flex", padding: "2px 8px", lineHeight: 0 }}>
                      <PartyTvCastGlyph support={featured.supportsTvCast} />
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 140,
                    position: "relative",
                  }}
                >
                  <PartyMascot shellId={featured.id} size={130} />
                </div>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    overflow: "hidden",
                    borderRadius: "inherit",
                  }}
                >
                  <div
                    className="party-anim-shine"
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      width: "40%",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)",
                    }}
                  />
                </div>
              </button>
            </section>
          )}

          <section style={{ padding: "18px 20px 4px", display: "flex", gap: 10, position: "relative", zIndex: 2, flexWrap: "wrap" }}>
            <button
              type="button"
              className="party-btn party-btn-hot"
              style={{ flex: isAppSubmitting ? "1 1 100%" : "2 1 200px" }}
              onClick={() => {
                landingCta("open_party_modes");
                navigate("/party/modes");
              }}
            >
              {t("landing.party.openDeck")}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M15 6l-6 6 6 6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {!isAppSubmitting && (
              <button
                type="button"
                className="party-btn party-btn-ghost"
                style={{ flex: "1 1 120px" }}
                onClick={() => {
                  landingCta("party_shop_from_hero");
                  goPurchase();
                }}
              >
                {t("landing.party.store")}
              </button>
            )}
          </section>

          <section style={{ padding: "22px 0 10px", position: "relative", zIndex: 2 }}>
            <div
              style={{
                padding: "0 20px 8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 8,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontFamily: "var(--font-display)",
                  fontSize: 22,
                  fontWeight: 800,
                }}
              >
                {t("landing.party.stripTitle")}
              </h2>
              <button
                type="button"
                onClick={() => {
                  landingCta("see_all_modes");
                  navigate("/party/modes");
                }}
                style={{
                  appearance: "none",
                  background: "transparent",
                  border: 0,
                  color: "var(--hot)",
                  fontWeight: 700,
                  fontFamily: "var(--font-display)",
                  fontSize: 14,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {t("landing.party.seeAll")} {i18n.language === "ar" ? "←" : "→"}
              </button>
            </div>
            <div
              className="party-scroll"
              style={{
                display: "flex",
                gap: 12,
                overflowX: "auto",
                padding: "4px 20px 14px",
                scrollSnapType: "x mandatory",
              }}
            >
              {games.map((g, i) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => {
                    landingCta("horizontal_game_strip", {
                      shell_id: g.id,
                      access: g.access,
                      position: i,
                    });
                    navigate(`/party/detail/${g.id}`);
                  }}
                  className="party-kan-card party-anim-card-deal"
                  style={
                    {
                      ["--card-bg" as string]: g.bg,
                      flex: "0 0 160px",
                      scrollSnapAlign: "start",
                      appearance: "none",
                      border: 0,
                      color: "var(--ink)",
                      fontFamily: "inherit",
                      padding: 14,
                      cursor: "pointer",
                      textAlign: "start",
                      height: 210,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      animationDelay: `${60 + i * 40}ms`,
                    } as React.CSSProperties
                  }
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div className="party-mono" style={{ fontSize: 10, color: "var(--ink-dim)", letterSpacing: "0.1em" }}>
                        #{String(i + 1).padStart(2, "0")}
                      </div>
                      <span className="party-chip" style={{ display: "inline-flex", padding: "2px 6px", lineHeight: 0 }}>
                        <PartyTvCastGlyph support={g.supportsTvCast} />
                      </span>
                    </div>
                    <PartyAccessBadge access={g.access} price={g.price} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <PartyMascot shellId={g.id} size={80} />
                  </div>
                  <div>
                    <div className="party-display" style={{ fontSize: 24, lineHeight: 1 }}>
                      {i18n.language === "ar" ? g.nameAr : g.nameEn}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 4 }}>{g.tag}</div>
                  </div>
                </button>
              ))}
            </div>
          </section>

          <footer
            style={{
              padding: "24px 20px 32px",
              borderTop: "1px solid var(--ink-faint)",
              marginTop: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 16,
                marginBottom: 20,
              }}
            >
              <a
                href="https://www.instagram.com/kanplays"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                style={{
                  color: "var(--ink)",
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  border: "1.5px solid var(--ink-faint)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InstagramIcon />
              </a>
              <a
                href="mailto:info@kanplays.com"
                aria-label="Email"
                style={{
                  color: "var(--ink)",
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  border: "1.5px solid var(--ink-faint)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EmailIcon />
              </a>
              <a
                href="https://www.tiktok.com/@kanplays"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                style={{
                  color: "var(--ink)",
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  border: "1.5px solid var(--ink-faint)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                </svg>
              </a>
            </div>
            {!isMobileApp && isNotMobileAppAndroid ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => landingCta("footer_app_store")}
                  style={{ display: "inline-block", lineHeight: 0 }}
                >
                  <img
                    src={`https://tools.applemediaservices.com/api/badges/download-on-the-app-store/white/${i18n.language?.startsWith("ar") ? "ar-sa" : "en-us"
                      }?size=250x83`}
                    alt={t("landing.party.downloadAppAlt")}
                    width={150}
                    height={50}
                    decoding="async"
                    style={{ height: 50, width: "auto", maxWidth: "min(220px, 85vw)" }}
                  />
                </a>
              </div>
            ) : null}
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 16, marginBottom: 16 }}>
              <button
                type="button"
                onClick={() => {
                  landingCta("footer_terms");
                  navigate("/terms-and-conditions");
                }}
                className="party-mono"
                style={{
                  background: "none",
                  border: 0,
                  color: "var(--ink-dim)",
                  fontSize: 11,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {t("landing.party.linkTerms")}
              </button>
              <button
                type="button"
                onClick={() => {
                  landingCta("footer_privacy");
                  navigate("/privacy-policy");
                }}
                className="party-mono"
                style={{
                  background: "none",
                  border: 0,
                  color: "var(--ink-dim)",
                  fontSize: 11,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {t("landing.party.linkPrivacy")}
              </button>
              <button
                type="button"
                onClick={() => {
                  landingCta("footer_contact");
                  navigate("/contact-us");
                }}
                className="party-mono"
                style={{
                  background: "none",
                  border: 0,
                  color: "var(--ink-dim)",
                  fontSize: 11,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {t("landing.party.linkContact")}
              </button>
            </div>
            <div
              className="party-mono"
              style={{
                textAlign: "center",
                color: "var(--ink-faint)",
                fontSize: 10,
                letterSpacing: "0.2em",
              }}
            >
              {t("landing.party.footer")}
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
